import React, { FC, useState } from 'react';
import {
  Fade,
  Grid,
  Box,
  TextField,
  MenuItem,
  FormControl,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Modal, Loader, Card, ModalSaveSection, SMBSelect } from '../../../components';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { IDropdownResponse, IPartGeneralInfo } from '../../../models';
import { Assignment } from '@mui/icons-material';
import { getUnitsOfMeasure, createPart } from '../../../fetch';
import { ManagePartsFiltersDropdown } from '../manage-parts/manage-parts-filters-dropdown';

interface ICreatePartModal {
  open: boolean;
  onClose: () => void;
}

export const CreatePartModal: FC<ICreatePartModal> = ({ open, onClose }) => {
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState<string>('');
  const [selectedPartClassIdFilter, setSelectedPartClassIdFilter] = useState(null);
  const [isPartActive, setIsPartActive] = useState<string>('');
  const [rounding, setRounding] = useState<string>('');
  const [unitOfMeasureId, setUnitOfMeasureId] = useState<number | string>('');
  const [quantityInterval, setQuantityInterval] = useState<number | string>('');
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    setUnitOfMeasureId('');
    setQuantityInterval('');
    setName('');
    setIsPartActive('');
    setRounding('');
    onClose();
  };

  const roundingOptions = [
    {
      value: 'Yes',
      description: 'Round Up'
    },
    {
      value: 'No',
      description: 'Round Down'
    }
  ];

  const { isLoading: isLoadingUnitsOfMeasure, data: unitsOfMeasure } = useQuery<
    IDropdownResponse[],
    Error
  >(['getUnitsOfMeasure'], () => getUnitsOfMeasure());

  return (
    <>
      <Modal
        title="Add New Part"
        open={open}
        onClose={() => {
          handleClose();
        }}
        maxWidth="md"
      >
        {isSaving && <Loader type="overlay" position="centered" />}
        <Fade in={open}>
          <Box mt={1}>
            <Card title="Info" icon={<Assignment />}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="Name"
                    name="name"
                    size="small"
                    value={name}
                    onChange={({ target: { value } }) => setName(value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    required
                    onChange={({ target: { value } }) => setUnitOfMeasureId(value)}
                    variant="outlined"
                    handleClear={() => setUnitOfMeasureId('')}
                    name={`unitOfMeasureId`}
                    label={`Unit of Measure`}
                    id={`unitOfMeasureId`}
                    value={unitOfMeasureId}
                    disabled={isLoadingUnitsOfMeasure}
                  >
                    {unitsOfMeasure &&
                      unitsOfMeasure.map((unit: any, index) => {
                        return (
                          <MenuItem key={`${index + 1}`} value={unit.value}>
                            {unit.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <ManagePartsFiltersDropdown
                      required={true}
                      partClassId={null}
                      depth={0}
                      setSelectedPartClassIdFilter={setSelectedPartClassIdFilter}
                      width={6}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="Quantity Interval"
                    name="quantityInterval"
                    size="small"
                    value={quantityInterval}
                    onChange={({ target: { value } }) => setQuantityInterval(value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    disabled={quantityInterval ? false : true}
                    onChange={({ target: { value } }) => setRounding(value)}
                    variant="outlined"
                    handleClear={() => setRounding('')}
                    name="rounding"
                    label="Rounding"
                    id="rounding"
                    value={rounding}
                  >
                    {roundingOptions &&
                      roundingOptions.map((rounding: any, index) => {
                        return (
                          <MenuItem key={`${index + 1}`} value={rounding.value}>
                            {rounding.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box padding="0.5rem">
                    <FormControl fullWidth variant="outlined" size="small" required>
                      <Typography>Is Part Active*</Typography>
                      <RadioGroup
                        sx={{
                          position: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'left',
                          alignItems: 'start'
                        }}
                        value={isPartActive}
                        onChange={({ target: { value } }) => setIsPartActive(value)}
                        aria-label="isPartActive"
                        name="isPartActive"
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </Card>
            <ModalSaveSection
              isSaveDisabled={
                !name || !unitOfMeasureId || !isPartActive || !selectedPartClassIdFilter
              }
              handleCancel={() => {
                handleClose();
              }}
              handleSave={async () => {
                try {
                  setIsSaving(true);
                  const data: IPartGeneralInfo = {
                    name: name,
                    partClassId: selectedPartClassIdFilter,
                    unitOfMeasureId: unitOfMeasureId,
                    isActive: isPartActive === 'Yes' ? true : false,
                    quantityInterval: quantityInterval,
                    roundUp: rounding === 'Yes' ? true : false
                  };
                  const part = await createPart(data);
                  enqueueSnackbar('Success, part created!', {
                    variant: 'success'
                  });

                  handleClose();
                  history.push(`/development/parts/manage-parts/${part.data}/general-information`);
                } catch (error) {
                  enqueueSnackbar(error?.Detail ?? `Error creating part, please try again.`, {
                    variant: 'error'
                  });
                  console.log(error);
                } finally {
                  setIsSaving(false);
                }
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
